import React, { useState, useEffect } from 'react';
import { UserData, ChineseZodiacSign } from '../types';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';
import { getChineseZodiacFromYear } from '../utils/calculator';
import { CHINESE_ZODIAC_DATA } from '../constants';
import ChineseZodiacIcon from '../components/ChineseZodiacIcon';
import chineseData from '../src/content/chinese.json';
import { Sparkles, ChevronRight, Moon } from 'lucide-react';

interface ChineseZodiacPageProps {
  userData: UserData;
}

const ChineseZodiacPage: React.FC<ChineseZodiacPageProps> = ({ userData }) => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Chinese Zodiac Calculator – Find Your Animal | ZodiAge',
    description: 'Discover your Chinese zodiac sign, personality traits, compatibility, and yearly insights using your birth year.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  const [birthYear, setBirthYear] = useState<string>('');
  const [zodiacSign, setZodiacSign] = useState<ChineseZodiacSign | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleReveal = () => {
    const year = parseInt(birthYear, 10);
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      setError('Please enter a valid year (e.g., 1990).');
      return;
    }
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      setZodiacSign(getChineseZodiacFromYear(year) as ChineseZodiacSign);
      setIsLoading(false);
    }, 900);
  };

  const currentYear = new Date().getFullYear();
  const dailyInsights = [
    "Today is a day for reflection and quiet contemplation.",
    "Embrace new opportunities with an open heart.",
    "Your creativity is at its peak. It's a good day to start a new project.",
    "Focus on your relationships and connections with others.",
    "A moment of mindfulness can bring clarity and peace."
  ];
  const [dailyInsight] = useState(dailyInsights[Math.floor(Math.random() * dailyInsights.length)]);

  return (
    <div className="space-y-12 animate-in fade-in duration-500 pb-12">

      {/* Birth Year Input Section */}
      <div id="input-section" className="max-w-md mx-auto bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 shadow-xl">
        <div className="space-y-2">
          <label htmlFor="birthYear" className="text-sm text-white font-medium">Enter Your Birth Year</label>
          <input
            type="number"
            id="birthYear"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            placeholder="e.g., 1990"
            className="w-full bg-input border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-yellow-500 outline-none transition-all h-[50px]"
          />
          <p className="text-xs text-gray-500">If you were born in January or February, your zodiac sign may belong to the previous lunar year.</p>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
        <button
          onClick={handleReveal}
          disabled={isLoading}
          className="w-full mt-4 bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold text-lg py-3 rounded-lg hover:from-purple-500 hover:to-pink-400 transition-all shadow-lg shadow-purple-600/50 flex items-center justify-center gap-2"
        >
          {isLoading ? <Sparkles className="animate-spin" /> : 'Reveal My Chinese Zodiac'}
        </button>
      </div>

      {/* Zodiage Result Card */}
      {zodiacSign && (
        <div className={`transition-all duration-700 ease-in-out transform ${isLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          <div className="max-w-3xl mx-auto bg-card rounded-3xl p-8 border border-yellow-500/30 shadow-2xl shadow-yellow-600/20 relative overflow-hidden">
            <div className="text-center mb-8">
              <ChineseZodiacIcon sign={zodiacSign} className="text-8xl mx-auto mb-4" />
              <h2 className="text-5xl font-display font-bold text-white">{zodiacSign}</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center">
              <div className="bg-cosmic-800/50 p-4 rounded-lg">
                <p className="text-xs text-gray-400">Element</p>
                <p className="font-bold text-white">{CHINESE_ZODIAC_DATA[zodiacSign].element}</p>
              </div>
              <div className="bg-cosmic-800/50 p-4 rounded-lg">
                <p className="text-xs text-gray-400">Yin/Yang</p>
                <p className="font-bold text-white">{CHINESE_ZODIAC_DATA[zodiacSign].yinYang}</p>
              </div>
              <div className="bg-cosmic-800/50 p-4 rounded-lg">
                <p className="text-xs text-gray-400">Lucky Colors</p>
                <p className="font-bold text-white">{CHINESE_ZODIAC_DATA[zodiacSign].luckyColors.join(', ')}</p>
              </div>
              <div className="bg-cosmic-800/50 p-4 rounded-lg">
                <p className="text-xs text-gray-400">Lucky Numbers</p>
                <p className="font-bold text-white">{CHINESE_ZODIAC_DATA[zodiacSign].luckyNumbers.join(', ')}</p>
              </div>
            </div>

            {/* Personality Overview */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-white text-center">Your Chinese Zodiac Personality</h3>
              <div>
                <h4 className="font-bold text-yellow-400">Strengths</h4>
                <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].strengths}</p>
              </div>
              <div>
                <h4 className="font-bold text-yellow-400">Challenges</h4>
                <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].challenges}</p>
              </div>
              <div>
                <h4 className="font-bold text-yellow-400">Love & Relationships</h4>
                <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].love}</p>
              </div>
              <div>
                <h4 className="font-bold text-yellow-400">Career & Talents</h4>
                <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].career}</p>
              </div>
            </div>

            {/* Compatibility Section */}
            <div className="mt-8 pt-6 border-t border-gray-800">
              <h3 className="text-2xl font-bold text-white text-center mb-4">Chinese Zodiac Compatibility</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <h4 className="font-semibold text-green-400">Best Matches</h4>
                  <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].bestMatches.join(', ')}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-400">Neutral Matches</h4>
                  <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].neutralMatches.join(', ')}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400">Challenging Matches</h4>
                  <p className="text-gray-300">{CHINESE_ZODIAC_DATA[zodiacSign].challengingMatches.join(', ')}</p>
                </div>
              </div>
            </div>
            
            {/* Current Year Insight */}
            <div className="mt-8 pt-6 border-t border-gray-800">
              <h3 className="text-2xl font-bold text-white text-center mb-4">What This Year Means for You</h3>
              <p className="text-gray-400 text-center italic">This year ({currentYear}) highlights reflection, growth, and strategic opportunities influenced by the current Chinese zodiac cycle.</p>
            </div>
            
            {/* Daily Insight */}
            <div className="mt-8 pt-6 border-t border-gray-800">
              <h3 className="text-2xl font-bold text-white text-center mb-4">Today's Zodiac Insight</h3>
              <p className="text-gray-300 text-center text-lg">"{dailyInsight}"</p>
            </div>
          </div>
        </div>
      )}
      {/* Chinese Zodiac Educational Content — Always visible for crawlers */}
      <article className="max-w-3xl mx-auto mb-12">
          <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
              <div className="seo-content" dangerouslySetInnerHTML={{ __html: chineseData.html }} />
          </div>
        {/* Disclaimer */}
        <section className="bg-yellow-900/20 border border-yellow-700/30 rounded-2xl p-5 mt-8">
          <div className="flex items-start gap-3">
            <Moon size={20} className="text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-white text-sm mb-1">About Chinese Astrology</h3>
              <p className="text-sm text-yellow-100 leading-relaxed">
                Chinese astrology is a rich cultural tradition spanning thousands of years. It is used for
                self-reflection, cultural celebration, and personal insight — not as empirical science or
                predictive fortune-telling. Use these interpretations as prompts for contemplation, not as
                deterministic life guidance.
              </p>
            </div>
          </div>

          {/* In-Article Ad for Chinese Zodiac Page */}
          <AdSpace type="in-article" slot="7716101506" className="my-8" />
        </section>
      </article>
    </div>
  );
};

export default ChineseZodiacPage;
